// the loop's order (Oct 27, programming-plan.md Phase D; config/clock.json `rotation`). A spine of glance screens in a
// fixed order with flavor slots (F) between them; each flavor slot draws from the deck the screen that has waited
// longest, never two of a kind back to back, with an alert screen (ws4kp's Hazards) first whenever it's up. The loop
// rules (rules.mjs) still decide which screens are ready. A show (shows.mjs) queues its screens ahead of all this.
// ws4kp's navigation asks `window.RHL.nextScreen()` when the loop moves forward (upstream patch 7); null leaves it to
// ws4kp's own order.
import { getDisplay, currentDisplay } from '../navigation.mjs';
import STATUS from '../status.mjs';
import { byHand } from './snapshot-display.mjs';

const { rotation: cfg } = window.RHL.clock;

let byElem = null;
const find = (elemId) => {
	if (!byElem) {
		byElem = {};
		for (let i = 0; i < 64; i += 1) {
			const display = getDisplay(i);
			if (display?.elemId) byElem[display.elemId] = display;
		}
	}
	return byElem[elemId];
};
const ready = (display) => !!display && display.status === STATUS.loaded && display.timing?.totalScreens > 0 && !display.isFeature;

let pos = 0;
let lastKind = null;
const lastAired = new Map();
const queue = [];

// the flavor slot's pick: an alert first, then the longest wait, not the kind that just aired (unless nothing else is
// ready), each at least once an hour by that rule
const pickFlavor = (kindBefore, current) => {
	const deck = Object.entries(cfg.deck).map(([id, kind]) => ({ id, kind, display: find(id) }))
		.filter((d) => ready(d.display) && d.display !== current);
	if (!deck.length) return null;
	const alert = deck.find((d) => d.kind === 'alert');
	if (alert) return alert;
	const fresh = deck.filter((d) => d.kind !== kindBefore);
	return (fresh.length ? fresh : deck).sort((a, b) => (lastAired.get(a.id) ?? 0) - (lastAired.get(b.id) ?? 0))[0];
};

// the next screen, from the show queue or the spine; `take` moves the rotation on, otherwise it's a look ahead
const next = ({ take = true } = {}) => {
	const current = currentDisplay();
	while (queue.length) {
		const item = queue[0];
		const display = typeof item === 'string' ? find(item) : item.display;
		if (!take) return display;
		queue.shift();
		item.before?.();
		// a show's screens play whatever the loop rules say (a show is when they're meant to air)
		if (display && byHand(() => display.status === STATUS.loaded)) return display;
	}
	let at = pos;
	let kind = lastKind;
	for (let tries = 0; tries < cfg.pattern.length; tries += 1) {
		const slot = cfg.pattern[at];
		at = (at + 1) % cfg.pattern.length;
		let pick = null;
		if (slot === 'F') {
			const flavor = pickFlavor(kind, current);
			if (flavor) {
				pick = flavor.display;
				kind = flavor.kind;
			}
		} else if (ready(find(slot)) && find(slot) !== current) pick = find(slot);
		if (pick) {
			if (take) {
				pos = at;
				lastKind = kind;
				lastAired.set(pick.elemId, Date.now());
			}
			return pick;
		}
	}
	return null;
};

const peekNext = () => next({ take: false });
const takeNext = () => next();
// after the :00 and :30 ID the loop starts fresh from the top of the spine
const restart = () => {
	pos = 0;
};
// a show: these screens next, in order, ahead of the spine; an entry is an element id or { display, before }
const enqueue = (items) => {
	queue.length = 0;
	queue.push(...items);
};
const showing = () => queue.length > 0;

// what ws4kp's navigation calls when the loop moves forward
window.RHL.nextScreen = () => takeNext();
window.RHL.rotationState = () => ({
	pos, lastKind, queue: queue.map((q) => (typeof q === 'string' ? q : q.display?.elemId)), aired: Object.fromEntries(lastAired),
});

export {
	peekNext, takeNext, restart, enqueue, showing, find,
};
